// @ts-nocheck
import type * as RCL from 'rclnodejs';

import type { CartesianPose } from '@sb/geometry';
import { targetToROS2Tooltip } from '@sb/geometry';
import type { StitchingConfigWithSpaceTravelParameters } from '@sb/integrations/implementations/WeldMachine/shared/types/StitchingConfig';
import type { WeavingConfig } from '@sb/integrations/implementations/WeldMachine/shared/types/WeavingConfig';
import { makeNamespacedLog } from '@sb/log';
import {
  DEFAULT_BASE_ACCELERATION_SCALING,
  DEFAULT_BASE_VELOCITY_SCALING,
  DEFAULT_MAX_TOOLTIP_SPEED,
  DEFAULT_WELD_TOOLTIP_ACCELERATION,
  type ArmTarget,
  type PoseArmTarget,
} from '@sb/motion-planning';
import { getRCL } from '@sb/ros/getRCL';
import type { SpeedProfile } from '@sb/routine-runner';
// Using explicit startWristPose from request; no caching needed

import { BaseAction } from './BaseAction';
import { JOINT_NAMES } from './constants';
import { PlanningGoalNotSucceededError } from './errors/PlanningGoalNotSucceededError';
import type { TaskFromRobotGoalActionRequest } from './TaskFromRobotGoalAction';

const log = makeNamespacedLog('CartesianPlanningAction');

const ZERO_POSE_ARM_TARGET: PoseArmTarget = {
  pose: {
    x: 0,
    y: 0,
    z: 0,
    i: 0,
    j: 0,
    k: 0,
    w: 1,
  },
  tcpOption: 'wrist',
};

// use same request format as TaskFromRobotGoalAction
export type CartesianPlanningActionRequest = TaskFromRobotGoalActionRequest & {
  startWristPose?: CartesianPose;
  startTooltipPose?: CartesianPose;
  weaveParameters?: WeavingConfig;
  stitchParameters?: StitchingConfigWithSpaceTravelParameters;
  toolFrame?: string;
};

export class CartesianPlanningAction extends BaseAction<
  'standard_bots_msgs/action/CartesianPlanning',
  RCL.standard_bots_msgs.action.CartesianPlanning_Goal,
  RCL.standard_bots_msgs.action.CartesianPlanning_Result
> {
  private request: CartesianPlanningActionRequest;

  public constructor(request: CartesianPlanningActionRequest) {
    log.info('constructor', 'Creating CartesianPlanningAction', request);
    super();
    this.request = request;
  }

  protected override getType(): RCL.TypeClass<keyof RCL.ActionsMap> {
    return 'standard_bots_msgs/action/CartesianPlanning';
  }

  protected override getTopic() {
    return '/cartesian_planning';
  }

  protected getTimeout(): number {
    return 60000;
  }

  protected override async createGoal() {
    const rcl = await getRCL();

    const CartesianPlanningActionPackage = rcl.require(
      'standard_bots_msgs/action/CartesianPlanning',
    );

    const goalMessage = new CartesianPlanningActionPackage.Goal();

    goalMessage.config = this.createCartesianPlanningMsg(rcl);

    log.info(
      'createGoal',
      `finished creating goal message`,
      goalMessage.toPlainObject(),
    );

    return goalMessage;
  }

  private createCartesianPlanningMsg(
    rcl: typeof RCL,
  ): RCL.standard_bots_msgs.msg.CartesianPlanning {
    const CartesianPlanningMsg = rcl.require(
      'standard_bots_msgs/msg/CartesianPlanning',
    );

    const cartesianPlanningMsg = rcl.createMessageObject(
      'standard_bots_msgs/msg/CartesianPlanning',
    );

    const isWeavingMotion = !!this.request.weaveParameters?.enabled;

    const isStitchingMotion = !!this.request.stitchParameters?.enabled;

    let planType = CartesianPlanningMsg.DIRECT_PLAN;

    if (isWeavingMotion) {
      planType = CartesianPlanningMsg.PATTERN_PLAN;
    } else if (isStitchingMotion) {
      planType = CartesianPlanningMsg.STITCH_PLAN;
    }

    log.info(
      'CartesianPlanningAction.createCartesianPlanningMsg',
      `Plan type: ${planType}`,
    );

    cartesianPlanningMsg.plan_type = planType;

    cartesianPlanningMsg.segments = [];
    cartesianPlanningMsg.blending_radius = [];
    cartesianPlanningMsg.segment_sampling_density = 0.001;

    // Always use the current start joint state provided in the request
    cartesianPlanningMsg.start_joint_state = this.createStartJointState(
      rcl,
      this.request.startJointState,
    );

    let targetIndex = 1;

    // Determine which start pose to use based on toolFrame
    const useWristFrame =
      !this.request.toolFrame || this.request.toolFrame === 'tool0';

    const startPose = useWristFrame
      ? this.request.startWristPose!
      : this.request.startTooltipPose!;

    const targetsWithStartPose: [PoseArmTarget, ...ArmTarget[]] = [
      {
        pose: startPose,
        tcpOption: useWristFrame ? 'wrist' : undefined,
      },
      ...this.request.targets,
    ];

    while (targetIndex < targetsWithStartPose.length) {
      // targets will either be linear or arc moves.
      // arcs will have two targets back-to-back
      const target: ArmTarget = targetsWithStartPose[targetIndex] as ArmTarget;
      const previousTarget = targetsWithStartPose[targetIndex - 1];

      if (target.motionKind === 'arc') {
        // create a segment for the arc
        const endTarget = targetsWithStartPose[targetIndex + 1] as ArmTarget;

        const arcSegment = this.createArcSegment(
          rcl,
          previousTarget as PoseArmTarget,
          target as PoseArmTarget,
          endTarget as PoseArmTarget,
        );

        cartesianPlanningMsg.segments.push(arcSegment);
        targetIndex += 2;

        if (targetIndex < targetsWithStartPose.length) {
          cartesianPlanningMsg.blending_radius.push(
            endTarget.blendRadius ?? 0.01,
          );
        }
      } else {
        // create a segment for the linear move
        const linearSegment = this.createLinearSegment(
          rcl,
          previousTarget as PoseArmTarget,
          target as PoseArmTarget,
        );

        cartesianPlanningMsg.segments.push(linearSegment);
        targetIndex += 1;

        if (targetIndex < targetsWithStartPose.length) {
          cartesianPlanningMsg.blending_radius.push(target.blendRadius ?? 0.01);
        }
      }
    }

    cartesianPlanningMsg.tool_poses = [];
    const numSegments = cartesianPlanningMsg.segments.length;

    if (numSegments === 0) {
      log.error(
        'createCartesianPlanningMsg',
        'No segments generated for Cartesian planning',
        {
          targetsLength: this.request.targets?.length ?? 0,
          isWeavingMotion,
        },
      );

      throw new Error(
        'No segments generated for Cartesian planning (ensure at least two pose targets after the start when weaving).',
      );
    }

    if (isWeavingMotion) {
      const weaveParamsMsg = this.buildWeaveParameters(rcl);

      if (weaveParamsMsg) {
        cartesianPlanningMsg.weave_parameters = weaveParamsMsg;
      }
    } else if (isStitchingMotion) {
      const stitchParamsMsg = this.buildStitchParameters(rcl);

      if (stitchParamsMsg) {
        cartesianPlanningMsg.stitch_parameters = stitchParamsMsg;
      }
    }

    // Set tool frame if specified (defaults to 'tool0' in message definition)
    if (this.request.toolFrame) {
      cartesianPlanningMsg.tool_frame = this.request.toolFrame;
    }

    Object.assign(
      cartesianPlanningMsg.plan_options,
      CartesianPlanningAction.buildPlanOptions(this.request.speedProfiles[0]),
    );

    return cartesianPlanningMsg;
  }

  private buildStitchParameters(rcl: typeof RCL) {
    const cfg: any = (this.request as any).stitchParameters;
    if (!cfg) return undefined;

    const stitchMsg = rcl.createMessageObject(
      'standard_bots_msgs/msg/StitchParameters',
    );

    stitchMsg.current = cfg.current;
    stitchMsg.stitch_length = cfg.stitch;
    stitchMsg.space_length = cfg.space;
    stitchMsg.space_translational_velocity = cfg.spaceTravelSpeed;
    stitchMsg.space_translational_acceleration = cfg.spaceTravelAcceleration;

    return stitchMsg;
  }

  private buildWeaveParameters(rcl: typeof RCL) {
    const cfg: any = (this.request as any).weaveParameters;
    if (!cfg) return undefined;

    const WeaveParametersMsg = rcl.require(
      'standard_bots_msgs/msg/WeaveParameters',
    );

    const weaveMsg = rcl.createMessageObject(
      'standard_bots_msgs/msg/WeaveParameters',
    );

    // Normalize pattern and map to ROS enum
    const { pattern } = cfg;

    if (pattern === 'zigzag') {
      weaveMsg.pattern = WeaveParametersMsg.PATTERN_ZIG_ZAG;
      // Expecting fields: amplitude (m), period (m), dwellLeft/right (seconds)
      const amp = Number(cfg.amplitude) || 0;
      const period = Number(cfg.period) || 0;
      const dwellLeftSec = (Number(cfg.dwellLeft) / 1000 || 0) as number;
      const dwellRightSec = (Number(cfg.dwellRight) / 1000 || 0) as number;

      weaveMsg.zigzag_params.amplitude = amp;
      weaveMsg.zigzag_params.period = period;
      weaveMsg.zigzag_params.dwell_left = dwellLeftSec;
      weaveMsg.zigzag_params.dwell_right = dwellRightSec;
    } else if (pattern === 'whip') {
      weaveMsg.pattern = WeaveParametersMsg.PATTERN_WHIP_STITCH;
      // Expecting fields: forwardOffset/reverseOffset (m), dwellForward/Reverse (seconds)
      const fwd = Number(cfg.forwardOffset) || 0;
      const rev = Number(cfg.reverseOffset) || 0;
      const dwellFwdSec = (Number(cfg.dwellForward) / 1000 || 0) as number;
      const dwellRevSec = (Number(cfg.dwellReverse) / 1000 || 0) as number;

      weaveMsg.whip_stitch_params.forward_offset = fwd;
      weaveMsg.whip_stitch_params.reverse_offset = rev;
      weaveMsg.whip_stitch_params.dwell_forward = dwellFwdSec;
      weaveMsg.whip_stitch_params.dwell_reverse = dwellRevSec;
    } else {
      throw new Error('Unsupported weave pattern');
    }

    return weaveMsg;
  }

  private createStartJointState(rcl: typeof RCL, positions: number[]) {
    const jointState = rcl.createMessageObject('sensor_msgs/msg/JointState');

    jointState.name = JOINT_NAMES;

    jointState.position = positions;

    return jointState;
  }

  private createLinearSegment(
    rcl: typeof RCL,
    start: PoseArmTarget,
    end: PoseArmTarget,
  ) {
    const Segment = rcl.require('standard_bots_msgs/msg/Segment');
    const segment = rcl.createMessageObject('standard_bots_msgs/msg/Segment');

    segment.segment_type = Segment.LINEAR_SEGMENT;

    segment.linear_segment_definition = [
      this.createCartesianPose(rcl, start),
      this.createCartesianPose(rcl, end),
    ];

    // TODO: remove this. RCL is complaining about circular segment being empty.
    segment.circular_segment_definition = [
      this.createCartesianPose(rcl, ZERO_POSE_ARM_TARGET),
      this.createCartesianPose(rcl, ZERO_POSE_ARM_TARGET),
      this.createCartesianPose(rcl, ZERO_POSE_ARM_TARGET),
    ];

    segment.positional_tolerance = 0.0001;
    // segment.roll_orientation_tolerance = 0.1;
    // segment.pitch_orientation_tolerance = 0.1;
    segment.yaw_orientation_tolerance = 0.0005;
    segment.positional_tolerance_increment = 0.00005;
    segment.orientation_tolerance_increment = 0.00025;
    segment.timing_type = Segment.CONSTANT_VELOCITY_TIMING;

    segment.translational_velocity =
      this.request.speedProfiles[0].maxTooltipSpeed ??
      DEFAULT_MAX_TOOLTIP_SPEED;

    segment.translational_acceleration =
      this.request.speedProfiles[0].maxTooltipAcceleration ??
      DEFAULT_WELD_TOOLTIP_ACCELERATION;

    return segment;
  }

  private createArcSegment(
    rcl: typeof RCL,
    start: PoseArmTarget,
    via: PoseArmTarget,
    end: PoseArmTarget,
  ) {
    const Segment = rcl.require('standard_bots_msgs/msg/Segment');
    const segment = rcl.createMessageObject('standard_bots_msgs/msg/Segment');

    segment.segment_type = Segment.CIRCULAR_SEGMENT;

    // TODO: remove this. RCL requires fixed-size arrays even for circular segments.
    segment.linear_segment_definition = [
      this.createCartesianPose(rcl, ZERO_POSE_ARM_TARGET),
      this.createCartesianPose(rcl, ZERO_POSE_ARM_TARGET),
    ];

    segment.circular_segment_definition = [
      this.createCartesianPose(rcl, start),
      this.createCartesianPose(rcl, via),
      this.createCartesianPose(rcl, end),
    ];

    segment.maintain_start_orientation =
      ('maintainStartOrientation' in end && end.maintainStartOrientation) ??
      false;

    segment.positional_tolerance = 0.0001;
    // segment.roll_orientation_tolerance = 0.1;
    // segment.pitch_orientation_tolerance = 0.1;
    segment.yaw_orientation_tolerance = 0.0005;
    segment.positional_tolerance_increment = 0.00005;
    segment.orientation_tolerance_increment = 0.00025;
    segment.timing_type = Segment.CONSTANT_VELOCITY_TIMING;

    segment.translational_velocity =
      this.request.speedProfiles[0].maxTooltipSpeed ??
      DEFAULT_MAX_TOOLTIP_SPEED;

    segment.translational_acceleration =
      this.request.speedProfiles[0].maxTooltipAcceleration ??
      DEFAULT_WELD_TOOLTIP_ACCELERATION;

    return segment;
  }

  private static buildPlanOptions(speedProfile: SpeedProfile) {
    return {
      velocity_scaling:
        speedProfile.baseVelocityScaling ?? DEFAULT_BASE_VELOCITY_SCALING,
      acceleration_scaling:
        speedProfile.baseAccelerationScaling ??
        DEFAULT_BASE_ACCELERATION_SCALING,
      max_velocity_limit: [...speedProfile.maxJointSpeeds],
      max_acceleration_limit: [...speedProfile.maxJointAccelerations],
      max_tooltip_velocity: speedProfile.maxTooltipSpeed,
      joint_torque_limits: [...speedProfile.maxJointTorques],
    };
  }

  private createCartesianPose(rcl: typeof RCL, target: PoseArmTarget) {
    const pose = rcl.createMessageObject('geometry_msgs/msg/Pose');
    const tooltipPose = targetToROS2Tooltip(target.pose);

    pose.position.x = tooltipPose.x;
    pose.position.y = tooltipPose.y;
    pose.position.z = tooltipPose.z;

    pose.orientation.x = tooltipPose.i;
    pose.orientation.y = tooltipPose.j;
    pose.orientation.z = tooltipPose.k;
    pose.orientation.w = tooltipPose.w;

    return pose;
  }

  protected override onGoalNotSucceeded(
    result: RCL.standard_bots_msgs.action.CartesianPlanning_Result | undefined,
  ): never {
    throw new PlanningGoalNotSucceededError(result?.error ?? '');
  }
}
